import { useEffect, useState, type ReactNode } from 'react';
import { useApi, validateKey } from './lib/api';
import { useTheme } from './lib/hooks';
import { DrawerProvider } from './lib/drawer';
import { NavProvider } from './lib/router';
import Gate from './components/Gate';
import Rail from './components/Rail';
import Topbar from './components/Topbar';
import Dashboard from './views/Dashboard';
import SearchView from './views/Search';
import ConsoleView from './views/Console';
import Curated from './views/Curated';
import ImportExport from './views/ImportExport';
import Normalized from './views/Normalized';
import Products from './views/Products';
import Product360 from './views/Product360';
import Internal from './views/Internal';
import ImportProducts from './views/ImportProducts';
import BrandsView from './views/Brands';
import Cache from './views/Cache';
import RequestLog from './views/RequestLog';
import Settings from './views/Settings';
import Stats from './views/Stats';
import Vehicles from './views/Vehicles';
import { UnifiedStub } from './views/Stubs';

/** view id → panel. `param` is only used by the views that take one. */
const VIEWS: Record<string, (param: string | null) => ReactNode> = {
  dashboard: () => <Dashboard />,
  search: () => <SearchView />,
  'u-vehicles': () => <UnifiedStub kind="vehicles" />,
  products: () => <Products />,
  internal: () => <Internal />,
  'import-products': () => <ImportProducts />,
  brands: () => <BrandsView />,
  product: (param) => <Product360 id={Number(param)} />,
  'kr-vehicles': () => <Vehicles source="kromeda" />,
  'kr-console': () => <ConsoleView source="kromeda" />,
  'kr-log': () => <RequestLog source="kromeda" />,
  'td-vehicles': () => <Vehicles source="tecdoc" />,
  'td-console': () => <ConsoleView source="tecdoc" />,
  'td-log': () => <RequestLog source="tecdoc" />,
  anticross: () => <Curated entity="anticross" />,
  'anticross-pp': () => <Curated entity="anticross-part-part" />,
  'xref-oe': () => <Curated entity="xref-oe-part" />,
  'xref-pp': () => <Curated entity="xref-part-part" />,
  import: () => <ImportExport />,
  parts: () => <Normalized kind="parts" />,
  'oe-codes': () => <Normalized kind="oe-codes" />,
  links: () => <Normalized kind="links" />,
  cache: () => <Cache />,
  log: () => <RequestLog source="all" />,
  stats: () => <Stats />,
  settings: () => <Settings />,
};

type GateState = 'checking' | 'locked' | 'open';

export default function App() {
  const { base, token, setConn } = useApi();
  const [gate, setGate] = useState<GateState>(token ? 'checking' : 'locked');
  const [theme, toggleTheme] = useTheme();

  // Re-validate a persisted key on load so a revoked/expired key drops to the gate.
  useEffect(() => {
    if (gate !== 'checking') return;
    let cancelled = false;
    validateKey(base, token).then((r) => {
      if (!cancelled) setGate(r === 'ok' ? 'open' : 'locked');
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function lock() {
    setConn({ token: '' });
    setGate('locked');
  }

  if (gate === 'checking') {
    return <div className="flex h-full items-center justify-center text-[13px] text-muted">Checking key…</div>;
  }
  if (gate === 'locked') {
    return <Gate onUnlock={() => setGate('open')} />;
  }

  return (
    <NavProvider>
      {({ view, param, go }) => (
        <DrawerProvider>
          <div className="grid h-screen" style={{ gridTemplateColumns: '228px 1fr' }}>
            <Rail view={view} onNav={go} />
            <main className="flex flex-col overflow-hidden">
              <Topbar view={view} theme={theme} onToggleTheme={toggleTheme} onLock={lock} />
              <div key={`${view}:${param}`} className="flex-1 overflow-y-auto px-5 pb-16 pt-5">
                {(VIEWS[view] ?? VIEWS.dashboard!)(param)}
              </div>
            </main>
          </div>
        </DrawerProvider>
      )}
    </NavProvider>
  );
}
